import { useState, useEffect, useCallback } from 'react';
import { connectionsAPI } from '../api/enhancedApiService';

/**
 * Hook to manage tier configuration for a connection
 * @param {string} connectionId - The connection ID to fetch tier config for
 */
export const useTierConfig = (connectionId) => {
  const [tierConfig, setTierConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch tier config when connection changes
  useEffect(() => {
    if (!connectionId) {
      setTierConfig(null);
      setLoading(false);
      return;
    }

    const fetchTierConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await connectionsAPI.getTierConfig(connectionId);
        setTierConfig(response.tier_config);
      } catch (err) {
        console.error('Error fetching tier config:', err);
        setError(err);
        // Set default config on error
        setTierConfig(getDefaultTierConfig());
      } finally {
        setLoading(false);
      }
    };

    fetchTierConfig();
  }, [connectionId]);

  // Update tier config
  const updateTierConfig = useCallback(async (newConfig) => {
    if (!connectionId) return false;

    setSaving(true);
    setError(null);
    try {
      await connectionsAPI.updateTierConfig(connectionId, newConfig);
      setTierConfig(newConfig);
      return true;
    } catch (err) {
      console.error('Error updating tier config:', err);
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [connectionId]);

  return {
    tierConfig,
    loading,
    error,
    saving,
    updateTierConfig
  };
};

/**
 * Get default tier configuration
 */
export const getDefaultTierConfig = () => ({
  tier1_discovery: { enabled: true, interval_minutes: 15 },
  tier2_schema: { enabled: true, interval_minutes: 60 },
  tier3_changes: { enabled: true, interval_minutes: 5 },
  tier4_profiling: { enabled: true, interval_minutes: 10080 },
  tier5_lineage: { enabled: false, interval_minutes: 1440 }
});

/**
 * Tier metadata for UI display
 */
export const TIER_METADATA = {
  tier1_discovery: {
    name: 'Discovery',
    description: 'Discovers databases, schemas, and tables. Fast lightweight scan.',
    cost: 'low',
    defaultInterval: 15,
    intervalOptions: [
      { value: 5, label: 'Every 5 minutes' },
      { value: 15, label: 'Every 15 minutes' },
      { value: 30, label: 'Every 30 minutes' },
      { value: 60, label: 'Every hour' },
      { value: 360, label: 'Every 6 hours' },
      { value: 1440, label: 'Daily' }
    ]
  },
  tier2_schema: {
    name: 'Schema Collection',
    description: 'Collects column names, types, constraints, and keys.',
    cost: 'low',
    defaultInterval: 60,
    intervalOptions: [
      { value: 15, label: 'Every 15 minutes' },
      { value: 30, label: 'Every 30 minutes' },
      { value: 60, label: 'Every hour' },
      { value: 360, label: 'Every 6 hours' },
      { value: 1440, label: 'Daily' },
      { value: 10080, label: 'Weekly' }
    ]
  },
  tier3_changes: {
    name: 'Change Detection',
    description: 'Detects schema changes since last collection. Very lightweight.',
    cost: 'very low',
    defaultInterval: 5,
    intervalOptions: [
      { value: 5, label: 'Every 5 minutes' },
      { value: 15, label: 'Every 15 minutes' },
      { value: 30, label: 'Every 30 minutes' },
      { value: 60, label: 'Every hour' }
    ]
  },
  tier4_profiling: {
    name: 'Data Profiling',
    description: 'Collects column statistics: nulls, distinct values, min/max. Used for anomaly detection.',
    cost: 'medium',
    defaultInterval: 10080,
    intervalOptions: [
      { value: 360, label: 'Every 6 hours' },
      { value: 1440, label: 'Daily' },
      { value: 10080, label: 'Weekly' },
      { value: 43200, label: 'Monthly' }
    ]
  },
  tier5_lineage: {
    name: 'Lineage Analysis',
    description: 'Analyzes query history to build data lineage graphs. Resource intensive.',
    cost: 'high',
    defaultInterval: 1440,
    intervalOptions: [
      { value: 1440, label: 'Daily' },
      { value: 10080, label: 'Weekly' },
      { value: 43200, label: 'Monthly' }
    ],
    optional: true
  }
};

/**
 * Get cost badge color
 */
export const getCostColor = (cost) => {
  switch (cost) {
    case 'very low':
      return 'bg-green-100 text-green-800';
    case 'low':
      return 'bg-green-100 text-green-700';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'high':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default useTierConfig;
